import React from 'react'

import { useField } from '@unform/core'

import PropTypes from 'prop-types'

import { Container } from './styles'

import { findArrayItemById } from '@shared/utils/findArrayItemById'

const Select = ({ name, label, placeholder, options, emitValue, onBlur, onFocus, mapOptionLabel, mapOptionValue, ...props }) => {
  const selectRef = React.useRef(null)

  const [isFocused, setIsFocused] = React.useState(false)
  const [isFilled, setIsFilled] = React.useState(false)

  const {
    fieldName,
    defaultValue,
    registerField,
    error
  } = useField(name)

  const initialValue = typeof defaultValue === 'object' ? defaultValue[mapOptionValue] : defaultValue !== undefined ? defaultValue : null

  const handleSelectBlur = React.useCallback(
    (...args) => {
      setIsFocused(false)
      setIsFilled(Boolean(selectRef.current?.value))
      onBlur(...args)
    },
    [onBlur, selectRef]
  )

  const handleSelectFocus = React.useCallback(
    (...args) => {
      setIsFocused(true)
      onFocus(...args)
    },
    [onFocus]
  )

  React.useEffect(() => {
    registerField({
      ref: selectRef,
      name: fieldName,
      path: 'value',
      getValue (select) {
        return emitValue
          ? select.current?.value
          : findArrayItemById(options, select.current?.value, mapOptionValue)
      },
      setValue (select, value) {
        if (select.current & value !== undefined) {
          select.current.value = typeof value === 'object' ? value[mapOptionValue] : value
        }
      }
    })
  }, [selectRef, registerField, options, initialValue, fieldName, emitValue, mapOptionValue])

  return (
    <Container
      data-testid="select-container"
      hasError={Boolean(error)}
      isFocused={isFocused}
      isFilled={isFilled}
      key={`${initialValue}-${options?.length}`}
    >
      {label && <label htmlFor={`select-${name}`}>{label}</label>}

      <select
        id={`select-${name}`}
        name={name}
        data-testid="select"
        ref={selectRef}
        onBlur={handleSelectBlur}
        onFocus={handleSelectFocus}
        defaultValue={initialValue}
        {...props}
      >
        <option value={''}>{placeholder || 'Selecione'}</option>

        {options.map((option) => (
          <option
            key={option[mapOptionValue]}
            value={option[mapOptionValue]}
          >
            {option[mapOptionLabel]}
          </option>
        ))}
      </select>
    </Container>
  )
}

Select.propTypes = {
  mapOptionValue: PropTypes.string,
  mapOptionLabel: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  emitValue: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any.isRequired
    })
  ).isRequired
}

Select.defaultProps = {
  mapOptionValue: 'id',
  mapOptionLabel: 'text',
  emitValue: false,
  onFocus: () => {},
  onChange: () => {},
  onBlur: () => {}
}

export default Select
